import React from "react";
import { useTranslation } from "react-i18next";
import {
  DollarSign,
  FileText,
  TrendingUp,
  AlertTriangle,
  LucideIcon,
} from "lucide-react";
import RevenueChart from "./RevenueChart";

interface KeyMetric {
  title: string;
  value: string;
  subtitle: string;
  icon: LucideIcon;
  color: "blue" | "green" | "orange" | "red";
}

const AccountantDashboard: React.FC = () => {
  const { t } = useTranslation();

  const keyMetrics: KeyMetric[] = [
    {
      title: "Total Invoiced",
      value: "$1.2M",
      subtitle: "Last 30 days",
      icon: DollarSign,
      color: "blue",
    },
    {
      title: "Pending Claims",
      value: "128",
      subtitle: "Awaiting approval",
      icon: FileText,
      color: "orange",
    },
    {
      title: "Cleared Revenue",
      value: "$980K",
      subtitle: "Last 30 days",
      icon: TrendingUp,
      color: "green",
    },
    {
      title: "Overdue Invoices",
      value: "12",
      subtitle: "Action required",
      icon: AlertTriangle,
      color: "red",
    },
  ];

  const getColorClasses = (color: KeyMetric["color"]) => {
    const colors = {
      blue: "bg-blue-50 dark:bg-primary/10 text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-500/20",
      green: "bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400 border-green-100 dark:border-green-500/20",
      orange: "bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-100 dark:border-orange-500/20",
      red: "bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border-red-100 dark:border-red-500/20",
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black pt-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {keyMetrics.map((metric, index) => {
          const IconComponent = metric.icon;
          return (
            <div
              key={index}
              className="bg-white dark:bg-[#1a1b1c] border border-gray-200 dark:border-gray-700 rounded-lg p-6"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {metric.title}
                  </p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                    {metric.value}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                    {metric.subtitle}
                  </p>
                </div>
                <div
                  className={`p-4 rounded-lg ${getColorClasses(metric.color)} ml-3`}
                >
                  <IconComponent className="w-6 h-6" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <RevenueChart />
    </div>
  );
};

export default AccountantDashboard; 